import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Bell, ChevronDown, Search, Package, Ban, ShieldCheck, UserRound,
  MapPin, Ticket, Wallet, Clock, X, RefreshCw, Users,
} from 'lucide-react'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'
import { useModalA11y } from '../hooks/useModalA11y'

const PAGE_SIZE = 20

type Customer = {
  id: string
  name?: string | null
  phone?: string | null
  email?: string | null
  is_active?: boolean
  created_at?: string
  orders_count?: number | string
  order_count?: number | string
  total_spent?: number | string
  last_order_at?: string | null
}

type DetailOrder = {
  id: string
  order_number?: string
  status?: string
  total?: number
  discount?: number
  payment_method?: string | null
  payment_mode?: string | null
  payment_status?: string | null
  created_at?: string
}
type DetailAddress = {
  id: string
  label?: string
  full_address?: string
  is_default?: boolean
  serviceable?: boolean | null
}
type CustomerDetail = {
  customer?: Customer
  orders?: DetailOrder[]
  addresses?: DetailAddress[]
}

type CustomerStats = {
  total_customers?: number
  new_today?: number
  active_customers?: number
  blocked_customers?: number
  lifetime_revenue?: number
  avg_customer_value?: number
}

type FilterKey = 'all' | 'active' | 'blocked' | 'new' | 'repeat' | 'high_value'
type SortKey = 'created_at' | 'name' | 'total_spent' | 'orders_count' | 'last_order_at'

const money = (value: number | string | undefined | null) => `₹${Number(value || 0).toLocaleString('en-IN')}`
const compactMoney = (value: number) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`
  return `₹${value.toLocaleString('en-IN')}`
}
const initials = (name?: string | null, phone?: string | null) => {
  const src = (name || '').trim()
  if (src) {
    const parts = src.split(/\s+/).slice(0, 2)
    return parts.map((p) => p[0]?.toUpperCase()).join('') || 'C'
  }
  const digits = (phone || '').replace(/\D/g, '')
  return digits.slice(-2) || 'C'
}
// Deterministic warm avatar tint derived from the id so each customer keeps a
// stable colour across renders.
const avatarTint = (seed: string) => {
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 360
  return `hsl(${h}, 45%, 42%)`
}

const ordersOf = (c: Customer) => Number(c.orders_count ?? c.order_count ?? 0)

type Segment = { label: string; bg: string; fg: string; border: string }
const segmentsFor = (c: Customer): Segment[] => {
  const out: Segment[] = []
  if (c.is_active === false) {
    out.push({ label: 'Blocked', bg: 'var(--color-error-bg)', fg: 'var(--color-error)', border: 'var(--color-error-border)' })
    return out
  }
  const spend = Number(c.total_spent || 0)
  const orders = ordersOf(c)
  const joined = c.created_at ? new Date(c.created_at).getTime() : 0
  const isNew = joined > 0 && Date.now() - joined <= 7 * 24 * 60 * 60 * 1000
  if (spend > 10000) out.push({ label: 'VIP', bg: 'var(--color-purple-bg)', fg: 'var(--color-purple)', border: 'var(--color-purple-border)' })
  else if (spend > 5000) out.push({ label: 'High Value', bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)', border: 'var(--color-warning-border)' })
  if (orders >= 2) out.push({ label: 'Repeat', bg: 'var(--color-info-bg)', fg: 'var(--color-info)', border: 'var(--color-info-border)' })
  if (isNew) out.push({ label: 'New', bg: 'var(--color-success-bg)', fg: 'var(--color-success)', border: 'var(--color-success-border)' })
  if (out.length === 0) out.push({ label: 'Active', bg: 'var(--color-success-bg)', fg: 'var(--color-success)', border: 'var(--color-success-border)' })
  return out
}

// Highlights the matched part of a string for the live search.
const Highlight = ({ text, term }: { text: string; term: string }) => {
  if (!term) return <>{text}</>
  const idx = text.toLowerCase().indexOf(term.toLowerCase())
  if (idx < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'var(--accent)', color: 'var(--color-forest-dark)', borderRadius: 3, padding: '0 1px' }}>
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </>
  )
}

export default function CustomersPage() {
  const colors = useColors()
  const { addToast } = useToast()

  const [rows, setRows] = useState<Customer[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [stats, setStats] = useState<CustomerStats | null>(null)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [sortBy, setSortBy] = useState<SortKey>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [detailById, setDetailById] = useState<Record<string, CustomerDetail>>({})
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null)

  const [notifyCustomerId, setNotifyCustomerId] = useState<string | null>(null)
  const [notifyTitle, setNotifyTitle] = useState('')
  const [notifyMessage, setNotifyMessage] = useState('')

  const closeNotify = () => {
    setNotifyCustomerId(null)
    setNotifyTitle('')
    setNotifyMessage('')
  }
  const notifyRef = useModalA11y(Boolean(notifyCustomerId), closeNotify)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await adminApi.getCustomers({
        search: debouncedSearch || undefined,
        sort: sortBy,
        order: sortOrder,
        page,
        limit: PAGE_SIZE,
      })
      // Backend envelope: { success, data: [ ...customers ], pagination: { total } }.
      const body = (response ?? {}) as {
        data?: unknown
        customers?: Customer[]
        total?: number
        pagination?: { total?: number }
      }
      const customers: Customer[] = Array.isArray(body)
        ? (body as Customer[])
        : Array.isArray(body.data)
          ? (body.data as Customer[])
          : Array.isArray(body.customers)
            ? body.customers
            : Array.isArray((body.data as { customers?: Customer[] } | undefined)?.customers)
              ? ((body.data as { customers?: Customer[] }).customers as Customer[])
              : []
      const totalCount = Number(
        body.pagination?.total
          ?? body.total
          ?? (body.data as { total?: number } | undefined)?.total
          ?? customers.length,
      )
      setRows(customers)
      setTotal(totalCount)
    } catch {
      setError('Unable to load customers.')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, sortBy, sortOrder, page])

  useEffect(() => {
    void fetchCustomers()
  }, [fetchCustomers])

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.getCustomerStats()
      const body = (response ?? {}) as { data?: CustomerStats } & CustomerStats
      setStats(body.data ?? body)
    } catch {
      /* stats are non-critical; header just hides counts */
    }
  }, [])

  useEffect(() => {
    void fetchStats()
  }, [fetchStats])

  const filteredRows = useMemo(() => {
    const now = Date.now()
    return rows.filter((row) => {
      if (filter === 'active') return row.is_active !== false
      if (filter === 'blocked') return row.is_active === false
      if (filter === 'repeat') return ordersOf(row) >= 2
      if (filter === 'new') {
        const joined = row.created_at ? new Date(row.created_at).getTime() : 0
        return joined > 0 && now - joined <= 7 * 24 * 60 * 60 * 1000
      }
      if (filter === 'high_value') return Number(row.total_spent || 0) > 5000
      return true
    })
  }, [rows, filter])

  const filterCounts = useMemo(() => {
    const now = Date.now()
    const within7 = (r: Customer) => {
      const j = r.created_at ? new Date(r.created_at).getTime() : 0
      return j > 0 && now - j <= 7 * 24 * 60 * 60 * 1000
    }
    return {
      all: rows.length,
      active: rows.filter((r) => r.is_active !== false).length,
      blocked: rows.filter((r) => r.is_active === false).length,
      repeat: rows.filter((r) => ordersOf(r) >= 2).length,
      new: rows.filter(within7).length,
      high_value: rows.filter((r) => Number(r.total_spent || 0) > 5000).length,
    } as Record<FilterKey, number>
  }, [rows])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null)
      return
    }
    setExpandedId(id)
    if (detailById[id]) return
    try {
      setDetailLoadingId(id)
      const detail = await adminApi.getCustomerDetail(id)
      const body = (detail ?? {}) as { data?: CustomerDetail } & CustomerDetail
      setDetailById((prev) => ({ ...prev, [id]: body.data ?? body ?? {} }))
    } catch {
      addToast('Failed to load customer detail', 'error')
      setDetailById((prev) => ({ ...prev, [id]: {} }))
    } finally {
      setDetailLoadingId(null)
    }
  }

  const onBlockToggle = async (row: Customer) => {
    try {
      if (row.is_active === false) {
        await adminApi.unblockCustomer(row.id)
        addToast('Customer unblocked', 'success')
      } else {
        await adminApi.blockCustomer(row.id)
        addToast('Customer blocked', 'info')
      }
      await fetchCustomers()
      void fetchStats()
    } catch {
      addToast('Action failed', 'error')
    }
  }

  const sendNotification = async () => {
    if (!notifyCustomerId || !notifyTitle.trim() || !notifyMessage.trim()) return
    try {
      await adminApi.sendCustomerNotification(notifyCustomerId, {
        title: notifyTitle.trim(),
        message: notifyMessage.trim(),
      })
      addToast('Notification sent', 'success')
      closeNotify()
    } catch {
      addToast('Failed to send notification', 'error')
    }
  }

  const term = debouncedSearch

  // ── shared style helpers ────────────────────────────────────────────────
  const card = {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 'var(--radius-card)',
    boxShadow: 'var(--shadow-card)',
  } as const
  const iconBtn = (active = false): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: 'var(--radius-button)',
    border: `1px solid ${colors.border}`,
    background: active ? 'var(--surface)' : colors.bgCard,
    color: colors.textPrimary,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  })

  const statCards: Array<{ label: string; value: string; accent?: boolean }> = [
    { label: 'Total Customers', value: stats ? Number(stats.total_customers || 0).toLocaleString('en-IN') : '-' },
    { label: 'New Today', value: stats ? Number(stats.new_today || 0).toLocaleString('en-IN') : '-' },
    { label: 'Active Customers', value: stats ? Number(stats.active_customers || 0).toLocaleString('en-IN') : '-' },
    { label: 'Blocked', value: stats ? Number(stats.blocked_customers || 0).toLocaleString('en-IN') : '-' },
    { label: 'Lifetime Revenue', value: stats ? compactMoney(Number(stats.lifetime_revenue || 0)) : '-', accent: true },
    { label: 'Avg Customer Value', value: stats ? money(stats.avg_customer_value) : '-' },
  ]

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <style>{`
        @keyframes cust-shimmer { 0% { background-position: -400px 0 } 100% { background-position: 400px 0 } }
        @keyframes cust-fade { from { opacity: 0; transform: translateY(-4px) } to { opacity: 1; transform: none } }
        .cust-skel { background: linear-gradient(90deg, var(--surface) 25%, var(--bg-secondary) 37%, var(--surface) 63%); background-size: 800px 100%; animation: cust-shimmer 1.4s infinite linear; border-radius: 8px; }
        .cust-row { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease; }
        .cust-row:hover { box-shadow: 0 6px 20px rgba(15,46,42,0.10); border-color: var(--accent); }
        .cust-expand { animation: cust-fade .2s ease both; }
        .cust-chip { transition: all .15s ease; }
      `}</style>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div>
        <h2 className="font-display" style={{ margin: 0, fontSize: 26, color: colors.textPrimary }}>Customers</h2>
        <p className="font-body" style={{ margin: '4px 0 0', color: colors.textMuted, fontSize: 14 }}>
          Manage customer accounts, order history, and communication
        </p>
      </div>

      {/* ── Quick stats ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        {statCards.map((s) => (
          <div key={s.label} style={{ ...card, padding: '14px 16px' }}>
            <div className="font-body" style={{ fontSize: 12, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{s.label}</div>
            <div className="font-display" style={{ fontSize: 24, fontWeight: 700, marginTop: 6, color: s.accent ? 'var(--accent-text)' : colors.textPrimary }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div style={{ ...card, padding: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 240 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: colors.textMuted }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone or email"
              className="font-body"
              style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 'var(--radius-input)', border: `1px solid ${colors.border}`, background: colors.bgPrimary, color: colors.textPrimary, fontSize: 14, outline: 'none' }}
            />
          </div>
          <select
            value={`${sortBy}:${sortOrder}`}
            onChange={(e) => { const [b, o] = e.target.value.split(':'); setSortBy(b as SortKey); setSortOrder(o as 'asc' | 'desc') }}
            className="font-body"
            style={{ padding: '9px 12px', borderRadius: 'var(--radius-input)', border: `1px solid ${colors.border}`, background: colors.bgPrimary, color: colors.textPrimary, fontSize: 14, cursor: 'pointer' }}
          >
            <option value="created_at:desc">Newest first</option>
            <option value="created_at:asc">Oldest first</option>
            <option value="total_spent:desc">Highest spend</option>
            <option value="orders_count:desc">Most orders</option>
            <option value="last_order_at:desc">Recently active</option>
            <option value="name:asc">Name A–Z</option>
          </select>
          <button
            onClick={() => { void fetchCustomers(); void fetchStats() }}
            title="Refresh"
            style={{ ...iconBtn(), width: 'auto', padding: '0 12px', gap: 6, fontSize: 13, fontWeight: 600 }}
            className="font-body"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {([
            ['all', 'All'], ['active', 'Active'], ['new', 'New'], ['repeat', 'Repeat'],
            ['high_value', 'High Value'], ['blocked', 'Blocked'],
          ] as Array<[FilterKey, string]>).map(([key, label]) => {
            const isActive = filter === key
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className="cust-chip font-body"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  border: `1px solid ${isActive ? 'var(--accent)' : colors.border}`,
                  background: isActive ? 'var(--accent)' : colors.bgPrimary,
                  color: isActive ? 'var(--color-forest-dark)' : colors.textSecondary,
                }}
              >
                {label}
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--radius-pill)',
                  background: isActive ? 'rgba(15,46,42,0.15)' : colors.surface,
                  color: isActive ? 'var(--color-forest-dark)' : colors.textMuted,
                }}>
                  {filterCounts[key]}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── List ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ ...card, padding: 16, display: 'flex', gap: 14, alignItems: 'center' }}>
              <div className="cust-skel" style={{ width: 46, height: 46, borderRadius: '50%' }} />
              <div style={{ flex: 1, display: 'grid', gap: 8 }}>
                <div className="cust-skel" style={{ width: '40%', height: 14 }} />
                <div className="cust-skel" style={{ width: '60%', height: 12 }} />
              </div>
              <div className="cust-skel" style={{ width: 120, height: 30, borderRadius: 'var(--radius-pill)' }} />
            </div>
          ))}
        </div>
      ) : error ? (
        <div style={{ ...card, padding: 24, color: 'var(--color-error)' }}>{error}</div>
      ) : filteredRows.length === 0 ? (
        <div style={{ ...card, padding: '56px 24px', textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: colors.surface, display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
            <Users size={32} color={colors.textMuted} />
          </div>
          <div className="font-display" style={{ fontSize: 18, color: colors.textPrimary }}>
            {term || filter !== 'all' ? 'No customers match your search' : 'No customers yet'}
          </div>
          <p className="font-body" style={{ margin: '6px auto 18px', maxWidth: 360, color: colors.textMuted, fontSize: 14 }}>
            {term || filter !== 'all'
              ? 'Try a different search term or clear your filters.'
              : 'New customers will appear here automatically as they sign up in the app.'}
          </p>
          {(term || filter !== 'all') ? (
            <button
              onClick={() => { setSearch(''); setFilter('all') }}
              className="font-body"
              style={{ padding: '9px 18px', borderRadius: 'var(--radius-button)', border: 'none', background: 'var(--accent)', color: 'var(--color-forest-dark)', fontWeight: 600, cursor: 'pointer' }}
            >
              Clear search & filters
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 10 }}>
            {filteredRows.map((row) => {
              const segs = segmentsFor(row)
              const isOpen = expandedId === row.id
              const blocked = row.is_active === false
              return (
                <Fragment key={row.id}>
                  <div className="cust-row" style={{ ...card, overflow: 'hidden' }}>
                    {/* main row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, flexWrap: 'wrap' }}>
                      {/* identity */}
                      <button
                        onClick={() => void toggleExpand(row.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', flex: '1 1 260px', minWidth: 220, padding: 0 }}
                      >
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <div style={{ width: 46, height: 46, borderRadius: '50%', background: avatarTint(row.id), color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 16 }} className="font-body">
                            {initials(row.name, row.phone)}
                          </div>
                          <span title="Phone verified" style={{ position: 'absolute', right: -2, bottom: -2, background: colors.bgCard, borderRadius: '50%', display: 'grid', placeItems: 'center', width: 18, height: 18 }}>
                            <ShieldCheck size={13} color="var(--color-success)" />
                          </span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="font-body" style={{ fontWeight: 700, fontSize: 15, color: colors.textPrimary }}>
                              <Highlight text={row.name || 'Unnamed customer'} term={term} />
                            </span>
                            <ChevronDown size={15} color={colors.textMuted} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }} />
                          </div>
                          <div className="font-body" style={{ fontSize: 12.5, color: colors.textMuted, marginTop: 2 }}>
                            <Highlight text={row.phone || 'No phone'} term={term} />
                            {row.created_at ? ` · since ${format(new Date(row.created_at), 'MMM yyyy')}` : ''}
                          </div>
                        </div>
                      </button>

                      {/* metric badges */}
                      <div style={{ display: 'flex', gap: 18, flex: '0 0 auto', padding: '0 4px' }}>
                        <Metric label="Orders" value={String(ordersOf(row))} colors={colors} />
                        <Metric label="Spend" value={money(row.total_spent)} colors={colors} />
                        <Metric label="Last order" value={row.last_order_at ? `${formatDistanceToNow(new Date(row.last_order_at))} ago` : '-'} colors={colors} />
                      </div>

                      {/* status segments */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: '1 1 auto', justifyContent: 'flex-end' }}>
                        {segs.map((s) => (
                          <span key={s.label} className="font-body" style={{ fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 'var(--radius-pill)', background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
                            {s.label}
                          </span>
                        ))}
                      </div>

                      {/* actions */}
                      <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                        <button title="View profile" onClick={() => void toggleExpand(row.id)} style={iconBtn(isOpen)}><UserRound size={16} /></button>
                        <button title="View orders" onClick={() => window.location.assign(`/orders?search=${encodeURIComponent(row.phone || '')}`)} style={iconBtn()}><Package size={16} /></button>
                        <button title="Send notification" onClick={() => { setNotifyCustomerId(row.id); setNotifyTitle('Rapidry update'); setNotifyMessage('Hi! We have an update for your account.') }} style={iconBtn()}><Bell size={16} /></button>
                        <button title={blocked ? 'Unblock customer' : 'Block customer'} onClick={() => void onBlockToggle(row)} style={{ ...iconBtn(), color: blocked ? 'var(--color-success)' : 'var(--color-error)' }}><Ban size={16} /></button>
                      </div>
                    </div>

                    {/* expanded panel */}
                    {isOpen ? (
                      <div className="cust-expand" style={{ borderTop: `1px solid ${colors.border}`, background: colors.bgSecondary, padding: 16 }}>
                        {detailLoadingId === row.id ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 12 }}>
                            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="cust-skel" style={{ height: 120 }} />)}
                          </div>
                        ) : (
                          <ExpandedPanel row={row} detail={detailById[row.id] || {}} colors={colors} />
                        )}
                      </div>
                    ) : null}
                  </div>
                </Fragment>
              )
            })}
          </div>

          {/* pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div className="font-body" style={{ color: colors.textMuted, fontSize: 13 }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString('en-IN')}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="font-body" style={{ padding: '8px 14px', borderRadius: 'var(--radius-button)', border: `1px solid ${colors.border}`, background: colors.bgCard, color: colors.textPrimary, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1, fontWeight: 600 }}>Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="font-body" style={{ padding: '8px 14px', borderRadius: 'var(--radius-button)', border: `1px solid ${colors.border}`, background: colors.bgCard, color: colors.textPrimary, cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1, fontWeight: 600 }}>Next</button>
            </div>
          </div>
        </>
      )}

      {/* ── Notify modal ─────────────────────────────────────────────────── */}
      {notifyCustomerId ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', zIndex: 50, padding: 16 }}>
          <div ref={notifyRef} role="dialog" aria-modal="true" style={{ width: 460, maxWidth: '100%', ...card, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 className="font-display" style={{ margin: 0, fontSize: 18, color: colors.textPrimary }}>Send notification</h3>
              <button onClick={closeNotify} style={iconBtn()}><X size={16} /></button>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <input value={notifyTitle} onChange={(e) => setNotifyTitle(e.target.value)} placeholder="Title" className="font-body" style={{ padding: '10px 12px', borderRadius: 'var(--radius-input)', border: `1px solid ${colors.border}`, background: colors.bgPrimary, color: colors.textPrimary, fontSize: 14 }} />
              <textarea value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)} rows={4} placeholder="Message" className="font-body" style={{ padding: '10px 12px', borderRadius: 'var(--radius-input)', border: `1px solid ${colors.border}`, background: colors.bgPrimary, color: colors.textPrimary, fontSize: 14, resize: 'vertical' }} />
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={closeNotify} className="font-body" style={{ padding: '9px 16px', borderRadius: 'var(--radius-button)', border: `1px solid ${colors.border}`, background: colors.bgCard, color: colors.textPrimary, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={() => void sendNotification()} className="font-body" style={{ padding: '9px 18px', borderRadius: 'var(--radius-button)', border: 0, background: 'var(--color-forest-dark)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Send</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ── sub-components ──────────────────────────────────────────────────────────

type Colors = ReturnType<typeof useColors>

function Metric({ label, value, colors }: { label: string; value: string; colors: Colors }) {
  return (
    <div style={{ minWidth: 64 }}>
      <div className="font-body" style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div className="font-body" style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, marginTop: 2 }}>{value}</div>
    </div>
  )
}

function StatPill({ label, value, colors }: { label: string; value: string; colors: Colors }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
      <span className="font-body" style={{ color: colors.textMuted }}>{label}</span>
      <span className="font-body" style={{ color: colors.textPrimary, fontWeight: 600 }}>{value}</span>
    </div>
  )
}

function PanelCard({ title, icon, colors, children }: { title: string; icon: ReactNode; colors: Colors; children: ReactNode }) {
  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ color: 'var(--accent-text)' }}>{icon}</span>
        <span className="font-body" style={{ fontWeight: 700, fontSize: 13, color: colors.textPrimary }}>{title}</span>
      </div>
      {children}
    </div>
  )
}

function ExpandedPanel({ row, detail, colors }: { row: Customer; detail: CustomerDetail; colors: Colors }) {
  const orders = detail.orders || []
  const addresses = detail.addresses || []
  const money2 = (v: number | string | undefined | null) => `₹${Number(v || 0).toLocaleString('en-IN')}`

  const completed = orders.filter((o) => o.status === 'delivered').length
  const cancelled = orders.filter((o) => ['cancelled', 'failed', 'refunded', 'returned'].includes(String(o.status))).length
  const inProgress = orders.length - completed - cancelled
  const lifetime = orders.filter((o) => String(o.payment_status) === 'paid').reduce((s, o) => s + Number(o.total || 0), 0)
  const aov = orders.length ? Math.round(orders.reduce((s, o) => s + Number(o.total || 0), 0) / orders.length) : 0
  const couponOrders = orders.filter((o) => Number(o.discount || 0) > 0)
  const totalDiscount = couponOrders.reduce((s, o) => s + Number(o.discount || 0), 0)
  const codCount = orders.filter((o) => String(o.payment_method || o.payment_mode).toLowerCase() === 'cod').length
  const onlineCount = orders.length - codCount
  const pendingPay = orders.filter((o) => String(o.payment_status) === 'pending').length
  const refunds = orders.filter((o) => String(o.payment_status) === 'refunded' || o.status === 'refunded').length

  const timeline: Array<{ label: string; at?: string }> = []
  if (row.created_at) timeline.push({ label: 'Customer registered', at: row.created_at })
  orders.slice(0, 5).forEach((o) => timeline.push({ label: `Order ${o.order_number || o.id?.slice(0, 6)} · ${o.status}`, at: o.created_at }))

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
      <PanelCard title="Personal information" icon={<UserRound size={15} />} colors={colors}>
        <StatPill label="Name" value={row.name || 'Not Provided'} colors={colors} />
        <StatPill label="Phone" value={row.phone || 'Not Provided'} colors={colors} />
        <StatPill label="Email" value={row.email || 'Not Provided'} colors={colors} />
        <StatPill label="Customer since" value={row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy') : 'Not Provided'} colors={colors} />
        <StatPill label="Status" value={row.is_active === false ? 'Blocked' : 'Active'} colors={colors} />
      </PanelCard>

      <PanelCard title="Order summary" icon={<Package size={15} />} colors={colors}>
        <StatPill label="Total orders" value={String(orders.length)} colors={colors} />
        <StatPill label="Completed" value={String(completed)} colors={colors} />
        <StatPill label="In progress" value={String(Math.max(0, inProgress))} colors={colors} />
        <StatPill label="Cancelled" value={String(cancelled)} colors={colors} />
        <StatPill label="Avg order value" value={money2(aov)} colors={colors} />
        <StatPill label="Lifetime spend" value={money2(lifetime)} colors={colors} />
      </PanelCard>

      <PanelCard title="Coupons & payments" icon={<Ticket size={15} />} colors={colors}>
        <StatPill label="Coupons used" value={String(couponOrders.length)} colors={colors} />
        <StatPill label="Total discount" value={money2(totalDiscount)} colors={colors} />
        <StatPill label="COD orders" value={String(codCount)} colors={colors} />
        <StatPill label="Online orders" value={String(onlineCount)} colors={colors} />
        <StatPill label="Pending payments" value={String(pendingPay)} colors={colors} />
        <StatPill label="Refunds" value={String(refunds)} colors={colors} />
      </PanelCard>

      <PanelCard title={`Saved addresses (${addresses.length})`} icon={<MapPin size={15} />} colors={colors}>
        {addresses.length === 0 ? (
          <div className="font-body" style={{ fontSize: 13, color: colors.textMuted }}>No saved addresses</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {addresses.slice(0, 4).map((a) => (
              <div key={a.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 10px', background: colors.bgSecondary }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                  <span className="font-body" style={{ fontWeight: 700, fontSize: 12.5, color: colors.textPrimary }}>{a.label || 'Address'}</span>
                  {a.is_default ? <Tag text="Default" tone="accent" /> : null}
                  {a.serviceable === false ? <Tag text="Non-serviceable" tone="error" /> : a.serviceable ? <Tag text="Serviceable" tone="success" /> : null}
                </div>
                <div className="font-body" style={{ fontSize: 12, color: colors.textMuted }}>{a.full_address || 'Not Provided'}</div>
              </div>
            ))}
          </div>
        )}
      </PanelCard>

      <PanelCard title="Activity timeline" icon={<Clock size={15} />} colors={colors}>
        {timeline.length === 0 ? (
          <div className="font-body" style={{ fontSize: 13, color: colors.textMuted }}>No activity yet</div>
        ) : (
          <div style={{ position: 'relative', paddingLeft: 14 }}>
            <div style={{ position: 'absolute', left: 4, top: 4, bottom: 4, width: 2, background: colors.border }} />
            {timeline.map((t, i) => (
              <div key={i} style={{ position: 'relative', marginBottom: 10 }}>
                <span style={{ position: 'absolute', left: -13, top: 3, width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />
                <div className="font-body" style={{ fontSize: 12.5, color: colors.textPrimary }}>{t.label}</div>
                {t.at ? <div className="font-body" style={{ fontSize: 11, color: colors.textMuted }}>{format(new Date(t.at), 'dd MMM yyyy, HH:mm')}</div> : null}
              </div>
            ))}
          </div>
        )}
      </PanelCard>

      <PanelCard title="Recent orders" icon={<Wallet size={15} />} colors={colors}>
        {orders.length === 0 ? (
          <div className="font-body" style={{ fontSize: 13, color: colors.textMuted }}>No orders</div>
        ) : (
          orders.slice(0, 6).map((o) => (
            <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0', borderBottom: `1px solid ${colors.border}` }}>
              <span className="font-body" style={{ color: colors.textPrimary }}>{o.order_number || o.id?.slice(0, 8)}</span>
              <span className="font-body" style={{ color: colors.textMuted }}>{o.status}</span>
              <span className="font-body" style={{ color: colors.textPrimary, fontWeight: 600 }}>{money2(o.total)}</span>
            </div>
          ))
        )}
      </PanelCard>
    </div>
  )
}

function Tag({ text, tone }: { text: string; tone: 'accent' | 'success' | 'error' }) {
  const map = {
    accent: { bg: 'var(--surface)', fg: 'var(--accent-text)', bd: 'var(--accent)' },
    success: { bg: 'var(--color-success-bg)', fg: 'var(--color-success)', bd: 'var(--color-success-border)' },
    error: { bg: 'var(--color-error-bg)', fg: 'var(--color-error)', bd: 'var(--color-error-border)' },
  }[tone]
  return (
    <span className="font-body" style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--radius-pill)', background: map.bg, color: map.fg, border: `1px solid ${map.bd}` }}>
      {text}
    </span>
  )
}
